/**
 * Screen Effects Config
 *
 * Visual tuning for full-screen overlay effects (screen flash, screen
 * tint) emitted by ScreenEffectManager. These cover the *entire live
 * canvas* — the viewport dimensions themselves are RUNTIME state read
 * from the live Pixi renderer / window (see
 * `src/game/effects/managers/viewport.ts`), NOT config. Only the
 * cosmetic knobs (default color, peak alpha, duration) live here.
 *
 * Two-section file (defaults+dev-override pattern):
 *   1. SCREEN_EFFECTS_DEFAULTS — immutable production values.
 *   2. SCREEN_EFFECTS_DEV_OVERRIDE — Partial<>; tweak here to iterate
 *      (e.g. `flashAlpha: 0.8` to make the flash obvious for QA).
 *
 * Consumers import `SCREEN_EFFECTS` (the merged result).
 *
 * Pattern docs: `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface ScreenEffectsConfig {
  /** Peak opacity of a screen flash at t=0, before it fades to 0 (0..1). */
  flashAlpha: number;
  /** Default flash color when a caller omits one (0xRRGGBB). */
  flashColor: number;
  /** Default flash fade duration in ms when a caller omits one. */
  flashDurationMs: number;
}

export const SCREEN_EFFECTS_DEFAULTS: ScreenEffectsConfig = {
  flashAlpha: 0.3,
  flashColor: 0xffffff,
  flashDurationMs: 200,
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to SCREEN_EFFECTS_DEFAULTS.
 *
 * Examples:
 *   flashAlpha: 0.8                 // crank the flash for QA visibility
 *   flashColor: 0xff00ff            // hot-pink flash to verify coverage
 *   flashDurationMs: 1200           // hold the flash long enough to screenshot
 */
export const SCREEN_EFFECTS_DEV_OVERRIDE: Partial<ScreenEffectsConfig> = {};

export const SCREEN_EFFECTS: ScreenEffectsConfig = {
  flashAlpha: SCREEN_EFFECTS_DEV_OVERRIDE.flashAlpha ?? SCREEN_EFFECTS_DEFAULTS.flashAlpha,
  flashColor: SCREEN_EFFECTS_DEV_OVERRIDE.flashColor ?? SCREEN_EFFECTS_DEFAULTS.flashColor,
  flashDurationMs: SCREEN_EFFECTS_DEV_OVERRIDE.flashDurationMs ?? SCREEN_EFFECTS_DEFAULTS.flashDurationMs,
};
